import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { SkillAvailableCurator } from '../../../models/skill-learning.model';

export interface SpecializationFormValue {
  name: string;
  description: string;
  curatorIds: string[];
}

const DEFAULT_SUBMIT_LABEL_KEY = 'SKILL_LEARNING_ADMIN_FORM_SAVE';
const CURATOR_WARNING_KEY = 'SKILL_LEARNING_ADMIN_FORM_CURATORS_REQUIRED';
const CURATOR_LIMIT_WARNING_KEY = 'SKILL_LEARNING_ADMIN_FORM_CURATORS_LIMIT_ONE';

@Component({
  selector: 'app-skill-specialization-form',
  templateUrl: './specialization-form.component.html',
  styleUrls: ['./specialization-form.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SkillSpecializationFormComponent implements OnInit {

  @Input() initialName: string = '';
  @Input() initialDescription: string = '';
  @Input() initialCuratorIds: string[] = [];
  @Input() availableCurators: SkillAvailableCurator[] = [];
  @Input() showCurators: boolean = false;
  @Input() isSubmitting: boolean = false;
  @Input() submitLabel: string = DEFAULT_SUBMIT_LABEL_KEY;
  @Input() requireCurator: boolean = false;
  @Input() maxCurators: number = 0;

  @Output() formSubmitted = new EventEmitter<SpecializationFormValue>();
  @Output() cancelRequested = new EventEmitter<void>();

  form: FormGroup;

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      name: [this.initialName, Validators.required],
      description: [this.initialDescription, Validators.required],
      curatorIds: [this.initialCuratorIds],
    });
  }

  get isFormInvalid(): boolean {
    if (this.form.invalid) return true;
    if (this.requireCurator && this.selectedCuratorCount === 0) return true;
    if (this.hasTooManyCurators) return true;
    return false;
  }

  get selectedCuratorCount(): number {
    const ids: string[] = this.form.get('curatorIds').value || [];
    return ids.length;
  }

  get hasNoCuratorsSelected(): boolean {
    return this.selectedCuratorCount === 0;
  }

  get hasTooManyCurators(): boolean {
    return this.maxCurators > 0 && this.selectedCuratorCount > this.maxCurators;
  }

  get curatorWarningMessage(): string {
    return CURATOR_WARNING_KEY;
  }

  get curatorLimitWarningMessage(): string {
    return CURATOR_LIMIT_WARNING_KEY;
  }

  get sortedCurators(): SkillAvailableCurator[] {
    const curators = this.availableCurators || [];
    return this.collectSelectedCurators(curators).concat(this.collectUnselectedCurators(curators));
  }

  isCuratorSelected(curatorId: string): boolean {
    const ids: string[] = this.form.get('curatorIds').value || [];
    return ids.indexOf(curatorId) !== -1;
  }

  toggleCurator(curatorId: string): void {
    const ids: string[] = this.form.get('curatorIds').value || [];
    const index = ids.indexOf(curatorId);
    const updated = this.buildUpdatedCuratorIds(ids, curatorId, index);
    this.form.get('curatorIds').setValue(updated);
  }

  buildCuratorFullName(curator: SkillAvailableCurator): string {
    return curator.firstName + ' ' + curator.lastName;
  }

  trackByCuratorId(index: number, curator: SkillAvailableCurator): string {
    return curator.id;
  }

  onSubmit(): void {
    if (this.isFormInvalid) {
      return;
    }
    this.formSubmitted.emit(this.form.value as SpecializationFormValue);
  }

  onCancel(): void {
    this.cancelRequested.emit();
  }

  private collectSelectedCurators(curators: SkillAvailableCurator[]): SkillAvailableCurator[] {
    return curators.filter(curator => this.isCuratorSelected(curator.id));
  }

  private collectUnselectedCurators(curators: SkillAvailableCurator[]): SkillAvailableCurator[] {
    return curators.filter(curator => !this.isCuratorSelected(curator.id));
  }

  private buildUpdatedCuratorIds(ids: string[], curatorId: string, index: number): string[] {
    if (index !== -1) {
      return ids.filter(id => id !== curatorId);
    }
    if (this.maxCurators === 1) {
      return [curatorId];
    }
    if (this.maxCurators > 0 && ids.length >= this.maxCurators) {
      return ids;
    }
    return ids.concat(curatorId);
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/components/admin/specialization-form/specialization-form.component.ts